/**
 * Shared edits from Check and Shape (port of steps.js): reject invalid input with the core's
 * message beside the field and never apply it; announce what changed.
 */
import type { BuildModel, Design } from '@/domain';
import {
  bayCount, bayWidthsOf, confirmField, describeChange, errorFor, evenBayWidths, FIELD_LABELS, fmtMm, layoutOf, limitsOf, mm, parseMm, setAppearance, setBack, setBays,
  setBayWidth, setDimension, setEven, setJoinery, setOpening, setPlinth, setShelfCount, setStock,
} from '@/domain';
import { familyOf } from '@/families/registry';
import { applyFix } from '@/families/fix';
import { setGridBaseKey } from '@/families/grid-bookcase/design';
import type { FrontSpec } from '@/families/fronts';
import { MATERIAL_CHOICES } from '@/families/stock';
import { announce } from './announce';
import { app, ui } from './state';

const derive = (d: Design): BuildModel => familyOf(d).generate(d, app.store.stock, app.store.construction);
/** Field name for a per-bay field (bay 1 keeps the first slice's names). */
export const bayField = (bay: number, base: string): string => (bay <= 0 ? base : `bay-${bay + 1}/${base}`);
/** The joint profile the current design is built to (other families carry their own envelope). */
export const constructionNow = () => (app.store.design.params ? app.store.build.construction : app.store.construction);
export const limits = () => limitsOf(constructionNow());
/** W, H or D limits for the current design (the widest piece depends on the bay count). */
export function limitsFor(key: 'W' | 'H' | 'D'): { min: number; max: number; label: string } {
  const L = limits();
  if (key !== 'W') return L[key];
  const bays = bayCount(app.store.design);
  if (bays === 1) return L.W;
  const rule = constructionNow().bays?.maxOverallWTicks ?? L.W.max * bays;
  return { ...L.W, max: Math.min(rule, L.W.max * bays) };
}

function newErrors(cand: BuildModel, current: BuildModel) {
  const cur = new Set(current.validation.errors.map((e) => e.message));
  return cand.validation.errors.filter((e) => !cur.has(e.message));
}
function setErr(key: string, msg: string): false {
  ui.fieldErrors[key] = msg; // announced once, by the role="alert" message
  return false;
}
export function clearErr(key: string, draftId?: string): void {
  delete ui.fieldErrors[key];
  if (draftId) delete ui.drafts[draftId];
}

export function commitDim(key: 'W' | 'H' | 'D', raw: string, draftId?: string): boolean {
  const store = app.store;
  const r = parseMm(raw);
  if (!r.ok) return setErr(key, r.error);
  const cand = setDimension(store.design, key, r.ticks, store.stock);
  const cb = derive(cand);
  const bad = errorFor(cb, key) || newErrors(cb, store.build)[0]?.message;
  if (bad) return setErr(key, bad);
  clearErr(key, draftId);
  if (cand[key] === store.design[key]) return true;
  const name = FIELD_LABELS[key]!;
  store.apply(() => cand, { label: `${name} ${fmtMm(r.ticks)} mm` });
  announce(`${name} set to ${fmtMm(r.ticks)} mm.${key === 'H' ? ` Openings now ${store.build.openings.map((o) => fmtMm(o.clear)).join(', ')} mm.` : ''}`);
  return true;
}

export function commitShelves(raw: string, draftId = 'chk-n', bay = 0): boolean {
  const store = app.store;
  const s = String(raw ?? '').trim();
  const L = limits();
  if (!/^\d+$/.test(s)) return setErr('n', `Enter the number of shelves as a whole number from ${L.n.min} to ${L.n.max}.`);
  const n = Number(s);
  if (n < L.n.min || n > L.n.max) return setErr('n', `Shelves must be between ${L.n.min} and ${L.n.max}.`);
  const cand = setShelfCount(store.design, n, store.stock, bay);
  const bad = errorFor(derive(cand), bayField(bay, 'n'));
  if (bad) return setErr('n', bad);
  clearErr('n', draftId);
  if (n === layoutOf(store.design, bay).n) return true;
  const multi = bayCount(store.design) > 1;
  store.apply(() => cand, { label: multi ? `Bay ${bay + 1}: ${n} shelves` : `${n} shelves` });
  const first = store.build.openings.find((o) => (o.bay ?? 0) === bay);
  announce(`${multi ? `Bay ${bay + 1}: ` : ''}${n} ${n === 1 ? 'shelf' : 'shelves'}. Openings spaced evenly at ${fmtMm(first?.clear ?? 0)} mm.`);
  return true;
}

export function commitOpening(i: number, raw: string, draftId?: string, bay = 0): boolean {
  const store = app.store;
  const key = bayField(bay, `opening-${i}`);
  const r = parseMm(raw);
  if (!r.ok) return setErr(key, r.error);
  const min = limits().minOpening;
  if (r.ticks < min) return setErr(key, `Opening ${i + 1} would be ${fmtMm(r.ticks)} mm; the minimum clear height is ${fmtMm(min)} mm.`);
  const before = layoutOf(store.design, bay).openings;
  const cand = setOpening(store.design, i, r.ticks, { rebalance: ui.rebalance, construction: store.construction, bay });
  const after = layoutOf(cand, bay).openings;
  if (ui.rebalance === 'neighbour' && after[i] !== r.ticks) {
    const j = i < before.length - 1 ? i + 1 : i - 1;
    const max = before[i]! + before[j]! - min;
    return setErr(key, `Opening ${i + 1} can be at most ${fmtMm(max)} mm here: opening ${j + 1} must keep its ${fmtMm(min)} mm minimum.`);
  }
  const bad = errorFor(derive(cand), key);
  if (bad) return setErr(key, bad);
  clearErr(key, draftId);
  if (after.join() === before.join()) return true;
  store.apply(() => cand, { label: `${bayCount(store.design) > 1 ? `Bay ${bay + 1} o` : 'O'}pening ${i + 1} ${fmtMm(r.ticks)} mm` });
  const b = store.build;
  const rem = b.bays[bay]?.remainder ?? b.remainder;
  announce(`Opening ${i + 1} set to ${fmtMm(r.ticks)} mm. ${rem === 0 ? 'All openings placed.' : errorFor(b, bayField(bay, 'openings')) ?? ''}`);
  return true;
}

export function setEvenTo(on: boolean, bay = 0): void {
  const store = app.store;
  if (!!layoutOf(store.design, bay).even === on) return;
  store.apply((d) => setEven(d, on, store.stock, bay), { label: on ? 'Even openings' : 'Even off' });
  const first = store.build.openings.find((o) => (o.bay ?? 0) === bay);
  announce(on ? `Even: every opening ${fmtMm(first?.clear ?? 0)} mm.` : 'Even off: openings kept as they are; height changes now scale them proportionally.');
}

// --- grid family parameters: bays, bay widths, back, plinth, joints --------------------------------

export function setBaysTo(n: number): boolean {
  const store = app.store;
  const rules = store.construction.bays;
  const min = rules?.min ?? 1, max = rules?.max ?? 4;
  if (n < min || n > max) return setErr('bays', `Bays must be between ${min} and ${max}.`);
  const cand = setBays(store.design, n, store.stock);
  const b = derive(cand);
  const bad = errorFor(b, 'bayWidths') ?? errorFor(b, 'W');
  if (bad) return setErr('bays', bad);
  clearErr('bays');
  if (n === bayCount(store.design)) return true;
  ui.bay = Math.min(ui.bay, n - 1);
  store.apply(() => cand, { label: `${n} bay${n === 1 ? '' : 's'}` });
  announce(`${n} bay${n === 1 ? '' : 's'}${n > 1 ? `, each ${fmtMm(bayWidthsOf(store.design)[0]!)} mm wide inside` : ''}.`);
  return true;
}

export function commitBayWidth(i: number, raw: string, draftId?: string): boolean {
  const store = app.store;
  const key = `bayWidth-${i}`;
  const r = parseMm(raw);
  if (!r.ok) return setErr(key, r.error);
  const min = store.construction.bays?.minBayClearTicks ?? mm(250);
  if (r.ticks < min) return setErr(key, `Bay ${i + 1} would be ${fmtMm(r.ticks)} mm wide; the narrowest bay is ${fmtMm(min)} mm.`);
  const cand = setBayWidth(store.design, i, r.ticks, store.construction, store.stock);
  if (bayWidthsOf(cand)[i] !== r.ticks) {
    const w = bayWidthsOf(store.design);
    const j = i < w.length - 1 ? i + 1 : i - 1;
    return setErr(key, `Bay ${i + 1} can be at most ${fmtMm(w[i]! + w[j]! - min)} mm wide here: bay ${j + 1} must keep its ${fmtMm(min)} mm.`);
  }
  clearErr(key, draftId);
  if (bayWidthsOf(cand).join() === bayWidthsOf(store.design).join()) return true;
  store.apply(() => cand, { label: `Bay ${i + 1} ${fmtMm(r.ticks)} mm wide` });
  announce(`Bay ${i + 1} set to ${fmtMm(r.ticks)} mm wide inside.`);
  return true;
}

export function evenBays(): void {
  app.store.apply((d) => evenBayWidths(d, app.store.stock), { label: 'Equal bays' });
  announce('Bays made equal widths.');
}

export function setBackTo(kind: 'none' | 'overlay' | 'rebated'): void {
  if ((app.store.design.back ?? 'overlay') === kind) return;
  app.store.apply((d) => setBack(d, kind), { label: kind === 'none' ? 'No back' : `${kind === 'overlay' ? 'Overlay' : 'Rebated'} back` });
  announce(kind === 'none' ? 'No back: fix the case to the wall to stop it racking.' : `${kind === 'overlay' ? 'Overlay' : 'Rebated'} back.`);
}

export function commitPlinth(raw: string, draftId?: string): boolean {
  const store = app.store;
  const r = parseMm(raw);
  if (!r.ok) return setErr('plinth', r.error);
  const max = store.construction.bays?.maxPlinthTicks ?? mm(150);
  if (r.ticks !== 0 && (r.ticks < mm(30) || r.ticks > max)) return setErr('plinth', `The plinth is 0 (none) or between 30 and ${fmtMm(max)} mm high.`);
  const cand = setPlinth(store.design, r.ticks, store.stock);
  const b = derive(cand);
  const bad = b.validation.errors.find((e) => e.field === 'plinth' || e.field.endsWith('n'))?.message;
  if (bad) return setErr('plinth', bad);
  clearErr('plinth', draftId);
  if ((cand.plinth ?? 0) === (store.design.plinth ?? 0)) return true;
  store.apply(() => cand, { label: r.ticks ? `Plinth ${fmtMm(r.ticks)} mm` : 'No plinth' });
  announce(r.ticks ? `Plinth ${fmtMm(r.ticks)} mm. Openings now ${store.build.openings.filter((o) => (o.bay ?? 0) === 0).map((o) => fmtMm(o.clear)).join(', ')} mm.` : 'No plinth.');
  return true;
}

export function setJoineryTo(kind: 'screwed-butt' | 'housed-dado'): void {
  if ((app.store.design.joinery ?? 'screwed-butt') === kind) return;
  app.store.apply((d) => setJoinery(d, kind), { label: kind === 'housed-dado' ? 'Shelves housed' : 'Shelves screwed' });
  announce(kind === 'housed-dado' ? 'Shelves housed into the sides: each shelf is longer by two housing depths.' : 'Shelves screwed between the sides.');
}

// --- families 2–4: generic parameters (plan §4.4), through the family's own behaviours ------------------

/** The current value of a family parameter (design.params), or undefined. */
export const familyParam = (key: string): unknown => (app.store.design.params ?? {})[key];

/** Set one family parameter; an edit that makes the design invalid is refused beside the control. */
export function setFamilyParam(key: string, value: unknown, label: string): boolean {
  const store = app.store;
  const fam = familyOf(store.design);
  if (!fam.setParam || !store.design.params) return false;
  const cand = fam.setParam(store.design, key, value);
  const bad = newErrors(derive(cand), store.build)[0]?.message;
  if (bad) return setErr(key, bad);
  clearErr(key);
  if (JSON.stringify(cand.params) === JSON.stringify(store.design.params)) return true;
  store.apply(() => cand, { label });
  announce(`${label}. ${store.build.panelCount} parts.`);
  return true;
}

/**
 * Set the design's doors and drawers (src/families/fronts.ts `FrontSpec[]`): top level on a grid, in `params`
 * elsewhere. Applied even when the result has an error, because every fronts error carries its fix and shows
 * beside the editor (a door with no board to hang from is a choice to change, not one to refuse).
 */
export function setFronts(next: readonly FrontSpec[], label: string): void {
  const store = app.store;
  const value = JSON.parse(JSON.stringify(next)) as unknown;
  store.apply((d) => (d.params ? (familyOf(d).setParam?.(d, 'fronts', value) ?? { ...d, params: { ...d.params, fronts: value } }) : { ...d, appearance: { ...d.appearance }, fronts: value as Record<string, unknown>[] }), { label });
  const n = store.build.validation.errors.filter((e) => e.field === 'fronts').length;
  announce(`${label}. ${n ? `${n} problem${n === 1 ? '' : 's'} with the doors and drawers.` : `${store.build.panelCount} parts.`}`);
}

/**
 * A top-level design key the grid family keeps outside `params` (base, plinth set-back, feet,
 * side style, slats…): set the way a family fix patch is (src/families/fix.ts), refused beside
 * the control when it makes the design invalid.
 */
export function setDesignKey(key: string, value: unknown, label: string): boolean {
  const store = app.store;
  // a grid's base keys go through the grid so the openings refit to the new clear height
  const cand = (!store.design.params ? setGridBaseKey(store.design, key, value, store.stock) : null) ?? applyFix(store.design, { label, patch: { [key]: value } });
  const bad = newErrors(derive(cand), store.build)[0]?.message;
  if (bad) return setErr(key, bad);
  clearErr(key);
  if (JSON.stringify(cand) === JSON.stringify(store.design)) return true;
  store.apply(() => cand, { label });
  announce(`${label}. ${store.build.panelCount} parts.`);
  return true;
}

/**
 * A per-row list of divider positions (`list` of ratios over W, e.g. the offset grid's
 * `rowDividerXs`, FND-21): set one row's dividers, refused beside the row when the result is
 * invalid. A hand edit makes a patterned layout free, when the family has a "free" pattern.
 */
export function setRowList(key: string, row: number, xs: readonly number[], label: string, rowCount = 0): boolean {
  const store = app.store;
  const fam = familyOf(store.design);
  const errKey = `${key}-${row}`;
  if (!fam.setParam || !store.design.params) return false;
  const raw = store.design.params[key];
  const cur: number[][] = Array.isArray(raw) ? (raw as number[][]).map((r) => r.slice()) : [];
  while (cur.length < rowCount) cur.push([]);
  const sorted = xs.map((x) => Math.round(x * 10000) / 10000).sort((a, b) => a - b);
  const next = cur.map((r, i) => (i === row ? sorted : r));
  let cand = fam.setParam(store.design, key, next);
  const pattern = fam.definition.parameters.find((p) => p.key === 'pattern' && p.options?.some((o) => o.value === 'free'));
  if (pattern && cand.params?.pattern !== 'free') cand = fam.setParam(cand, 'pattern', 'free');
  const bad = newErrors(derive(cand), store.build)[0]?.message;
  if (bad) return setErr(errKey, bad);
  clearErr(errKey);
  if (JSON.stringify(cand.params) === JSON.stringify(store.design.params)) return true;
  store.apply(() => cand, { label });
  announce(`${label}. ${store.build.panelCount} parts.`);
  return true;
}

// --- Look ---------------------------------------------------------------------------------------------

/** Material (plan §3 step 4): a stock change is a manufacturing change and says what it re-sized. */
export function setMaterialTo(stockId: string): void {
  const store = app.store;
  if (store.design.stockProfileId === stockId) return;
  const choice = MATERIAL_CHOICES.find((m) => m.stockId === stockId) ?? MATERIAL_CHOICES.find((m) => m.thicker?.some((t) => t.stockId === stockId));
  const before = store.build;
  const from = store.design.stockProfileId;
  const cand = setStock(store.design, stockId);
  store.apply(() => cand, { label: `Material ${choice?.label ?? stockId}` });
  const after = store.build;
  // a stock that breaks a valid design is remembered so Look and Pack can offer to keep the old one
  if (before.validation.ok && !after.validation.ok) ui.stockBreak = { from, to: stockId };
  else if (after.validation.ok) ui.stockBreak = null;
  const resized = after.parts.filter((p) => { const q = before.parts.find((x) => x.id === p.id); return !q || q.finished.l !== p.finished.l || q.finished.w !== p.finished.w || q.finished.t !== p.finished.t; }).length;
  const glued = after.parts.filter((p) => p.glueUp).length;
  announce(`${choice?.label ?? stockId}: ${resized} part${resized === 1 ? '' : 's'} re-sized${glued ? `, ${glued} glued up from strips` : ''}. ${describeChange(before.design, after.design)[0] ?? ''}`.trim());
}

export function setFinishTo(finish: 'oiled' | 'satin' | 'matt'): void {
  if (app.store.design.appearance.finish === finish) return;
  app.store.apply((d) => setAppearance(d, { finish }), { label: `Finish ${finish}` });
  announce(`Finish: ${finish === 'oiled' ? 'oiled' : finish === 'satin' ? 'satin lacquer' : 'matt lacquer'}. Nothing that gets cut changed.`);
}
export function setHardwareShown(on: boolean): void {
  app.store.apply((d) => setAppearance(d, { hardware: on }), { label: on ? 'Fixings shown' : 'Fixings hidden' });
}
export function setStagingTo(staging: 'studio' | 'room'): void {
  if (app.store.design.appearance.staging === staging) return;
  app.store.apply((d) => setAppearance(d, { staging }), { label: staging === 'room' ? 'Room' : 'Studio' });
}

export function setProps(on: boolean): void {
  app.store.apply((d) => setAppearance(d, { props: on }), { label: on ? 'Books shown' : 'Books hidden' });
}
export function rename(name: string): void {
  const n = name.trim() || 'Untitled bookcase';
  if (n === app.store.design.appearance.name) return;
  app.store.apply((d) => setAppearance(d, { name: n }), { label: 'Renamed' });
}

/**
 * The Shape remainder fix: "Add 12 mm to the top opening" / "Take … from the top opening",
 * for the first bay that has a remainder (the grid family's `remainder` behaviour).
 */
export function shapeFix(): { label: string; reason: string; bay: number } | null {
  const b = app.store.build;
  const multi = b.bays.length > 1;
  for (const bay of b.bays) {
    if (bay.remainder !== 0 && layoutOf(app.store.design, bay.index).openings.length) {
      const where = multi ? ` in bay ${bay.index + 1}` : '';
      return {
        bay: bay.index,
        label: bay.remainder > 0 ? `Add ${fmtMm(bay.remainder)} mm to the top opening${where}` : `Take ${fmtMm(-bay.remainder)} mm from the top opening${where}`,
        reason: errorFor(b, bayField(bay.index, 'openings')) ?? '',
      };
    }
  }
  return null;
}
export function placeRemainderTop(bay = shapeFix()?.bay ?? 0): void {
  const store = app.store;
  const g = layoutOf(store.design, bay).openings;
  const top = g.length - 1;
  const rem = store.build.bays[bay]?.remainder ?? store.build.remainder;
  store.apply((d) => setOpening(d, top, g[top]! + rem, { rebalance: 'none', construction: store.construction, bay }), { label: 'Placed the remainder' });
  announce('Remainder placed in the top opening. All openings placed.');
}
export function confirmKey(key: string, label: string): void {
  app.store.apply(() => ({ fields: confirmField(app.store.fields, key) }), { label: `Confirmed ${label.toLowerCase()}` });
}
export const mmStep = (ticks: number, step: number): number => ticks + mm(step);
